import * as THREE from 'three';

/**
 * TeleportSpell creates a dissolve/materialize particle effect.
 * The wizard dissolves into particles at the old position,
 * particles stream to the new position, and the wizard materializes.
 * Grants 0.5s invulnerability during the teleport.
 */
export class TeleportSpell {
  private scene: THREE.Scene;
  private disposed: boolean = false;
  private animationId: number | null = null;

  constructor(scene: THREE.Scene, fromPosition: THREE.Vector3, toPosition: THREE.Vector3) {
    this.scene = scene;
    this.createEffect(fromPosition, toPosition);
  }

  private createEffect(fromPos: THREE.Vector3, toPos: THREE.Vector3): void {
    const particleCount = 80;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const startPositions: THREE.Vector3[] = [];
    const endPositions: THREE.Vector3[] = [];

    for (let i = 0; i < particleCount; i++) {
      // Start scattered around old position (dissolve)
      const startOffset = new THREE.Vector3(
        (Math.random() - 0.5) * 1.5,
        Math.random() * 3,
        (Math.random() - 0.5) * 1.5,
      );
      const start = fromPos.clone().add(startOffset);
      startPositions.push(start);

      // End scattered around new position (materialize)
      const endOffset = new THREE.Vector3(
        (Math.random() - 0.5) * 1.5,
        Math.random() * 3,
        (Math.random() - 0.5) * 1.5,
      );
      const end = toPos.clone().add(endOffset);
      endPositions.push(end);

      positions[i * 3] = start.x;
      positions[i * 3 + 1] = start.y;
      positions[i * 3 + 2] = start.z;

      // Purple-blue-white streaming particles
      const t = Math.random();
      if (t < 0.3) {
        colors[i * 3] = 0.6; colors[i * 3 + 1] = 0.3; colors[i * 3 + 2] = 1;
      } else if (t < 0.6) {
        colors[i * 3] = 0.4; colors[i * 3 + 1] = 0.5; colors[i * 3 + 2] = 1;
      } else {
        colors[i * 3] = 0.8; colors[i * 3 + 1] = 0.8; colors[i * 3 + 2] = 1;
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
    });

    const particles = new THREE.Points(geometry, material);
    this.scene.add(particles);

    // Afterimage at old position - fading silhouette
    const afterimageGeo = new THREE.CylinderGeometry(0.5, 0.5, 3, 8);
    const afterimageMat = new THREE.MeshBasicMaterial({
      color: 0x8844FF,
      transparent: true,
      opacity: 0.4,
    });
    const afterimage = new THREE.Mesh(afterimageGeo, afterimageMat);
    afterimage.position.copy(fromPos);
    afterimage.position.y += 1.5;
    this.scene.add(afterimage);

    // Flash at departure
    const departLight = new THREE.PointLight(0x8844FF, 3, 10);
    departLight.position.copy(fromPos);
    departLight.position.y += 1;
    this.scene.add(departLight);

    // Flash at arrival
    const arriveLight = new THREE.PointLight(0x8844FF, 0, 10);
    arriveLight.position.copy(toPos);
    arriveLight.position.y += 1;
    this.scene.add(arriveLight);

    const totalDuration = 0.6; // seconds
    let elapsed = 0;

    const animate = () => {
      if (this.disposed) return;

      elapsed += 0.016;
      const progress = Math.min(1, elapsed / totalDuration);

      // Smoothstep for natural movement
      const t = progress * progress * (3 - 2 * progress);

      const pos = geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particleCount; i++) {
        // Lerp from start to end with some arc
        const arcHeight = Math.sin(t * Math.PI) * 2;
        pos[i * 3] = startPositions[i].x + (endPositions[i].x - startPositions[i].x) * t;
        pos[i * 3 + 1] = startPositions[i].y + (endPositions[i].y - startPositions[i].y) * t + arcHeight;
        pos[i * 3 + 2] = startPositions[i].z + (endPositions[i].z - startPositions[i].z) * t;
      }
      geometry.attributes.position.needsUpdate = true;

      // Fade in/out: fully visible in middle, fade at edges
      const opacity = progress < 0.2 ? progress / 0.2 :
                      progress > 0.8 ? (1 - progress) / 0.2 : 1;
      material.opacity = opacity;

      // Afterimage fades out
      afterimageMat.opacity = 0.4 * Math.max(0, 1 - progress / 0.5);

      // Lights transition
      departLight.intensity = 3 * Math.max(0, 1 - progress / 0.4);
      arriveLight.intensity = 3 * Math.max(0, (progress - 0.5) / 0.5);

      if (elapsed < totalDuration) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.animationId = null;
        // Final materialize flash
        arriveLight.intensity = 4;
        setTimeout(() => {
          this.scene.remove(particles);
          this.scene.remove(afterimage);
          this.scene.remove(departLight);
          this.scene.remove(arriveLight);
          geometry.dispose();
          material.dispose();
          afterimageGeo.dispose();
          afterimageMat.dispose();
          departLight.dispose();
          arriveLight.dispose();
        }, 200);
      }
    };

    this.animationId = requestAnimationFrame(animate);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }
}
